import * as Sentry from '@sentry/react'
import Api from '@vacationtracker/shared/services/api'
import { CrispSegmentsEnum } from '../types/crisp'
import { yieldToCrisp } from './crisp-priority'

type CrispSessionData = {
  [key: string]: string | number | boolean
}

const CRISP_TRIAL_TRIGGER_STORAGE_KEY = 'crispTrialTriggerExposed'
const TRIAL_TRIGGER_RECEIVED_SEGMENT = 'segment_trial_trigger_received'
const TRIAL_TRIGGER_RESPONDED_SEGMENT = 'segment_trial_trigger_responded'
const TRIAL_TRIGGER_EXPOSURE_TTL_MS = 30 * 60 * 1000

interface CrispTrialTriggerExposure {
  trigger: string
  timestamp: number
}

const isTrialTriggerExposed = (): boolean => {
  try {
    const raw = localStorage.getItem(CRISP_TRIAL_TRIGGER_STORAGE_KEY)
    if (!raw) {
      return false
    }
    const exposure = JSON.parse(raw) as CrispTrialTriggerExposure
    if (Date.now() - exposure.timestamp > TRIAL_TRIGGER_EXPOSURE_TTL_MS) {
      localStorage.removeItem(CRISP_TRIAL_TRIGGER_STORAGE_KEY)
      return false
    }
    return true
  } catch {
    return false
  }
}

export const markCrispTrialTriggerReceived = (trigger: string): void => {
  if (!window.$crisp) {
    return
  }
  window.$crisp.push(['set', 'session:segments', [[TRIAL_TRIGGER_RECEIVED_SEGMENT], true]])
  try {
    const payload: CrispTrialTriggerExposure = { trigger, timestamp: Date.now() }
    localStorage.setItem(CRISP_TRIAL_TRIGGER_STORAGE_KEY, JSON.stringify(payload))
  } catch (err) {
    Sentry.captureException(err)
  }
}

export const attachCrispTrialTriggerResponseListener = (): void => {
  if (!window.$crisp) {
    return
  }
  window.$crisp.push([
    'on',
    'message:sent',
    () => {
      if (isTrialTriggerExposed()) {
        window.$crisp.push(['set', 'session:segments', [[TRIAL_TRIGGER_RESPONDED_SEGMENT], true]])
      }
    },
  ])
}

export const setCrispSessionInfo = (segments: CrispSegmentsEnum[], sessionData: CrispSessionData) => {
  try {
    if (!window.$crisp) {
      return
    }

    window.$crisp.push(['set', 'session:segments', [segments, true]])
    setCrispSessionData(sessionData)
  } catch (err) {
    // Swallow errors
    Sentry.captureException(err)
  }
}

export const setCrispSessionData = (sessionData: CrispSessionData) => {
  if (!window.$crisp) {
    return
  }
  window.$crisp.push(['set', 'session:data', [Object.entries(sessionData)]])
}

export const openChat = (externalLink = false): void => {
  yieldToCrisp()
  if (window && window.$crisp) {
    window.$crisp.push(['do', 'chat:open'])
    return
  }
  if (externalLink) {
    window.open('https://vacationtracker.io/knowledge-base/', '_blank')?.focus()
  }
}

export const runCrispTrigger = (trigger: string): Promise<void> => {
  if (!window.$crisp) {
    return Promise.resolve()
  }
  yieldToCrisp()
  window.$crisp.push(['set', 'session:data', [[['trialTrigger', 'true']]]])

  const sessionId = (window.$crisp as unknown as { get: (key: string) => string | null })?.get?.('session:identifier')

  if (sessionId) {
    Api.post('/core/schedule-crisp-reset', { sessionId }).catch(() => {})
  }

  return new Promise((resolve) => {
    setTimeout(() => {
      window.$crisp.push(['do', 'trigger:run', [trigger]])
      resolve()
    }, 3000)
  })
}

// Kill-switch for the role gating: by default the chat is limited to admins, location admins
// and approvers; set REACT_APP_CRISP_FOR_ALL_ROLES=true to show it to every role again.
// Vee's system prompt (backend leave-assistant buildSupportContactSection) hardcodes the same gating.
export const isCrispLimitedToApproversAndAdmins = (): boolean => process.env.REACT_APP_CRISP_FOR_ALL_ROLES !== 'true'

export interface ICanRoleOpenSupportChatParams {
  role: string
  isApprover: boolean
}

// THE single place deciding who gets the support chat. Any non-User role (Admin, LocationAdmin…)
// and approvers qualify; everyone qualifies while the kill-switch env var is on.
export const canRoleOpenSupportChat = (params: ICanRoleOpenSupportChatParams): boolean => {
  const { role, isApprover } = params
  return !isCrispLimitedToApproversAndAdmins() || role !== 'User' || isApprover
}

let chatHidden = false

export const hideChat = (): void => {
  chatHidden = true
  window.$crisp && window.$crisp.push(['do', 'chat:hide'])
}

export const showChat = (): void => {
  chatHidden = false
  window.$crisp && window.$crisp.push(['do', 'chat:show'])
}

export const isChatHidden = (): boolean => chatHidden

export const hideAvailabilityTooltip = (): void => {
  window.$crisp && window.$crisp.push(['config', 'availability:tooltip', [false]])
}

export const sendChatMessage = (message: string): void => {
  window.$crisp && window.$crisp.push(['do', 'message:send', ['text', message]])
}
